import type { PodMethod } from "@khazzin/api-client";
import { MEDIA_REF } from "../../offline/media";

/**
 * The rules of the proof of delivery (EP10-S18, `03/06` §3), apart from the
 * screen so a test can read them. They mirror the server's
 * `shipment.Pod.Satisfies`, which the delivery's guard enforces again: the
 * phone only decides when **Complete delivery** may be pressed.
 */

/** The most photos one delivery keeps (DA-10-18). */
export const MAX_PHOTOS = 3;

/** A proof in progress, checkpointed after every step. */
export interface PodDraft {
	kind: "pod";
	/** What the proof closes: a delivery, or a partial one with its kept lines. */
	outcome: "success" | "partial";
	kept?: Record<string, number>;
	/** The photos' ids in the `media` store, in the order taken. */
	photoIds: string[];
	signatureId?: string;
	recipientName: string;
	/** The server matched the recipient's code. */
	otpVerified: boolean;
	/** The driver used the code's fallback: a photo and who took the parcel. */
	fallback: boolean;
}

export function emptyPod(outcome: PodDraft["outcome"], kept?: Record<string, number>): PodDraft {
	return {
		kind: "pod",
		outcome,
		...(kept === undefined ? {} : { kept }),
		photoIds: [],
		recipientName: "",
		otpVerified: false,
		fallback: false,
	};
}

/** Whether a proof in progress holds something a close would lose (ADJ-M03-4). */
export function podCaptured(d: PodDraft): boolean {
	return (
		d.photoIds.length > 0 ||
		d.signatureId !== undefined ||
		d.recipientName.trim() !== "" ||
		d.otpVerified
	);
}

/**
 * The methods the checklist shows: the store's required set, or, when it is
 * empty (the platform default), all three — a photo, and a signature or the
 * code.
 */
export function checklist(required: readonly PodMethod[]): PodMethod[] {
	return required.length > 0 ? [...required] : ["photo", "signature", "otp"];
}

/** Whether one method has its evidence. The code's fallback is a photo and a name. */
export function done(method: PodMethod, d: PodDraft): boolean {
	switch (method) {
		case "photo":
			return d.photoIds.length > 0;
		case "signature":
			return d.signatureId !== undefined;
		default:
			return (
				d.otpVerified || (d.fallback && d.photoIds.length > 0 && d.recipientName.trim() !== "")
			);
	}
}

/** Whether the proof satisfies the store's required set: Complete delivery's gate. */
export function satisfied(required: readonly PodMethod[], d: PodDraft): boolean {
	if (required.length === 0) {
		return done("photo", d) && (done("signature", d) || done("otp", d));
	}
	return required.every((m) => done(m, d));
}

/** The `attach_pod` payload: the files by their phone-local names, swapped at sync. */
export function podPayload(d: PodDraft, attemptKey: string, capturedAt: string) {
	const methods: PodMethod[] = [];
	if (d.photoIds.length > 0) {
		methods.push("photo");
	}
	if (d.signatureId !== undefined) {
		methods.push("signature");
	}
	if (d.otpVerified) {
		methods.push("otp");
	}
	const name = d.recipientName.trim();
	return {
		attemptKey,
		methods,
		capturedAt,
		...(d.photoIds.length > 0 ? { photoBlobRefs: d.photoIds.map((id) => MEDIA_REF + id) } : {}),
		...(d.signatureId === undefined ? {} : { signatureBlobRef: MEDIA_REF + d.signatureId }),
		...(name === "" ? {} : { recipientName: name }),
		...(d.fallback && !d.otpVerified ? { fallbackUsed: true } : {}),
	};
}

/** The recipient's number with the middle hidden: `+961 •• ••• 123`. */
export function maskPhone(phone: string): string {
	const digits = phone.replace(/[^\d+]/g, "");
	if (digits.length < 7) {
		return "•••";
	}
	return `${digits.slice(0, 4)} •• ••• ${digits.slice(-3)}`;
}

/** Seconds before the code may be sent again (DA-10-18). */
export const RESEND_AFTER_S = 30;
